import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Loader2,
  AlertCircle,
  CreditCard,
  Plus,
  CheckCircle2,
  Download,
  ShieldAlert,
  RotateCcw,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SiteNav from "../components/Layout/SiteNav";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";
import {
  getSolutionsPortalMe,
  openSolutionsBilling,
  getSolutionsChangeRequests,
  createSolutionsChangeRequest,
  getSolutionsInvoices,
  cancelSolutionsSubscription,
  downloadSolutionsExport,
  reactivateSolutionsSubscription,
} from "../services/solutionsApi";

const INVOICE_STATUS_STYLES = {
  paid: { bg: BRAND.sage + "22", fg: BRAND.sage, label: "Paid" },
  open: { bg: BRAND.gold + "22", fg: BRAND.goldDeep, label: "Due" },
  uncollectible: { bg: BRAND.terracotta + "22", fg: BRAND.terracotta, label: "Unpaid" },
  void: { bg: BRAND.muted + "22", fg: BRAND.muted, label: "Void" },
};

const CLIENT_STATUS_STYLES = {
  lead: { bg: BRAND.muted + "22", fg: BRAND.muted, label: "Lead" },
  offer_sent: { bg: BRAND.gold + "22", fg: BRAND.goldDeep, label: "Offer sent" },
  in_development: { bg: BRAND.gold + "22", fg: BRAND.goldDeep, label: "In development" },
  active: { bg: BRAND.sage + "22", fg: BRAND.sage, label: "Active" },
  past_due: { bg: BRAND.terracotta + "22", fg: BRAND.terracotta, label: "Past due" },
  suspended: { bg: BRAND.terracotta + "33", fg: BRAND.terracotta, label: "Suspended" },
  canceled: { bg: BRAND.muted + "22", fg: BRAND.muted, label: "Canceled" },
};

const REQUEST_STATUS_STYLES = {
  open: { bg: BRAND.muted + "22", fg: BRAND.muted, label: "Open" },
  in_progress: { bg: BRAND.gold + "22", fg: BRAND.goldDeep, label: "In progress" },
  done: { bg: BRAND.sage + "22", fg: BRAND.sage, label: "Done" },
  rejected: { bg: BRAND.terracotta + "22", fg: BRAND.terracotta, label: "Rejected" },
};

const PRIORITIES = ["low", "normal", "high"];

const inputClass =
  "h-12 rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]";

function StatusPill({ status, styles }) {
  const s = styles[status] || {
    bg: BRAND.muted + "22",
    fg: BRAND.muted,
    label: status || "Unknown",
  };
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold uppercase"
      style={{ background: s.bg, color: s.fg, letterSpacing: "0.12em" }}
    >
      {s.label}
    </span>
  );
}

const formatMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString("en-US")}`;
};

// Billing and invoice URLs come from our own backend, which gets them from
// Stripe, so they are trusted today. Check the scheme anyway before using one as
// a navigation target: rel="noreferrer" does not stop a javascript: href, so
// anything that ever put an attacker influenced value in these fields would
// execute on click.
const isHttpsUrl = (value) => {
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const invoiceLink = (invoice) => {
  const url = invoice?.invoicePdf || invoice?.hostedInvoiceUrl;
  return isHttpsUrl(url) ? url : null;
};

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, "MMMM d, yyyy");
};

export default function SolutionsPortal() {
  const navigate = useNavigate();
  const palette = useEditorialPalette();

  const inputStyle = {
    background: palette.paper,
    borderColor: palette.hairline,
    color: palette.ink,
    fontFamily: EDITORIAL_FONT.sans,
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: palette.muted,
    fontFamily: EDITORIAL_FONT.sans,
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [client, setClient] = useState(null);

  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");

  const [changeRequests, setChangeRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState("");

  const [invoices, setInvoices] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState("");

  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelNotice, setCancelNotice] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState("");

  const handleExport = async () => {
    setExportError("");
    try {
      setExportBusy(true);
      await downloadSolutionsExport();
    } catch (err) {
      console.error("export error", err);
      setExportError("We could not build your file. Please try again or email us.");
    } finally {
      setExportBusy(false);
    }
  };

  const [showNewRequest, setShowNewRequest] = useState(false);
  const [newRequest, setNewRequest] = useState({
    title: "",
    priority: "normal",
    description: "",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  const loadChangeRequests = async () => {
    try {
      setRequestsLoading(true);
      setRequestsError("");
      const data = await getSolutionsChangeRequests();
      setChangeRequests(Array.isArray(data?.changeRequests) ? data.changeRequests : []);
    } catch (err) {
      console.error("load change requests error", err);
      setRequestsError("Unable to load change requests right now.");
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setInvoicesLoading(true);
      setInvoicesError("");
      const data = await getSolutionsInvoices();
      setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
      setPaymentMethod(data?.paymentMethod || null);
    } catch (err) {
      console.error("load invoices error", err);
      setInvoicesError("Unable to load your billing history right now.");
    } finally {
      setInvoicesLoading(false);
    }
  };

  const refreshClient = async () => {
    try {
      const data = await getSolutionsPortalMe();
      if (data?.ok && data.client) setClient(data.client);
    } catch (err) {
      console.error("refresh client error", err);
    }
  };

  const handleCancel = async (e) => {
    e.preventDefault();
    setCancelError("");
    try {
      setCancelBusy(true);
      const data = await cancelSolutionsSubscription(cancelReason.trim());
      const ends = formatDate(data?.endsAt);
      setCancelNotice(
        ends
          ? `Your subscription is cancelled. You keep access until ${ends}, and you will not be charged again.`
          : "Your subscription is cancelled. You will not be charged again."
      );
      setShowCancel(false);
      setCancelReason("");
      await refreshClient();
    } catch (err) {
      console.error("cancel error", err);
      setCancelError(
        err?.response?.data?.message || "Unable to cancel right now. Please contact us."
      );
    } finally {
      setCancelBusy(false);
    }
  };

  const handleReactivate = async () => {
    setCancelError("");
    try {
      setCancelBusy(true);
      await reactivateSolutionsSubscription();
      setCancelNotice("Your subscription is active again. Billing will continue as normal.");
      await refreshClient();
    } catch (err) {
      console.error("reactivate error", err);
      setCancelError(
        err?.response?.data?.message || "Unable to reactivate right now. Please contact us."
      );
    } finally {
      setCancelBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Stripe returns the customer here with ?paid=1 the moment checkout
    // succeeds, which usually beats the webhook that flips the project to
    // active. Without this the customer sees "Offer sent" and "Access
    // disabled" on the screen that is meant to confirm their payment, so poll
    // briefly for the activation instead of showing stale state.
    const justPaid = new URLSearchParams(window.location.search).get("paid") === "1";
    const DEADLINE_MS = 20000;
    const INTERVAL_MS = 1500;

    const loadPortal = async () => {
      const startedAt = Date.now();
      try {
        setLoading(true);
        setError("");

        for (;;) {
          const data = await getSolutionsPortalMe();
          if (cancelled) return;

          if (!data?.ok || !data.client) {
            // A brand new client may not be readable for a moment either.
            if (justPaid && Date.now() - startedAt < DEADLINE_MS) {
              await new Promise((r) => setTimeout(r, INTERVAL_MS));
              continue;
            }
            setNotFound(true);
            return;
          }

          setClient(data.client);

          // A customer arriving straight from checkout lands in_development,
          // not active: their down payment is in and the build starts. Both
          // count as settled, or the page would keep polling until it gave up.
          const settled =
            data.client.status === "active" ||
            data.client.status === "in_development" ||
            !justPaid;
          if (settled || Date.now() - startedAt >= DEADLINE_MS) {
            loadChangeRequests();
            loadInvoices();
            return;
          }
          await new Promise((r) => setTimeout(r, INTERVAL_MS));
        }
      } catch (err) {
        if (cancelled) return;
        console.error("load portal error", err);
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("We could not load your project right now. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPortal();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManageBilling = async () => {
    try {
      setBillingLoading(true);
      setBillingError("");
      const data = await openSolutionsBilling();
      if (data?.ok && isHttpsUrl(data.url)) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setBillingError(data?.error || "Unable to open billing right now.");
      }
    } catch (err) {
      console.error("billing error", err);
      setBillingError(
        err?.response?.data?.error || "Unable to open billing right now."
      );
    } finally {
      setBillingLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    setCreateError("");
    setCreateSuccess("");

    if (!newRequest.title.trim()) {
      setCreateError("Please enter a title for your request.");
      return;
    }
    if (!newRequest.description.trim()) {
      setCreateError("Please describe the change you need.");
      return;
    }

    try {
      setCreating(true);
      const data = await createSolutionsChangeRequest({
        title: newRequest.title.trim(),
        priority: newRequest.priority,
        description: newRequest.description.trim(),
      });
      if (data?.ok === false) {
        setCreateError(data?.error || "Unable to send your request.");
        return;
      }
      setNewRequest({ title: "", priority: "normal", description: "" });
      setShowNewRequest(false);
      setCreateSuccess("Request sent to the Muslim+ team.");
      loadChangeRequests();
    } catch (err) {
      console.error("create change request error", err);
      if (err?.response?.status === 429) {
        setCreateError(
          "You have sent a few requests recently. Please wait a bit and try again."
        );
      } else {
        setCreateError(
          err?.response?.data?.error || "Unable to send your request."
        );
      }
    } finally {
      setCreating(false);
    }
  };

  const offer = client?.offer || null;
  const downPayment = formatMoney(offer?.downPayment);
  const monthly = formatMoney(offer?.monthly);
  const nextPayment = formatDate(client?.nextPaymentDate);
  // While we are building, the down payment is paid and the monthly fee has
  // not started. There is no next payment date to show, and saying so plainly
  // matters more here than anywhere else on the page.
  const billingOnHold = client?.billingOnHold === true;
  const billingStarts = formatDate(client?.billingStartsAt);

  return (
    <EditorialShell>
      <SiteNav />
      <PatternTile opacity={0.04} />

      <main
        className="relative mx-auto px-4 py-14 sm:px-6 sm:py-20 lg:px-10"
        style={{ maxWidth: 960 }}
      >
        {/* Header */}
        <div className="mb-10 space-y-4 text-center">
          <div className="inline-flex">
            <Eyebrow>Muslim+ Solutions</Eyebrow>
          </div>
          <SerifHeading
            level={1}
            style={{
              fontSize: "clamp(2.25rem, 5.5vw, 4rem)",
              lineHeight: 0.98,
              letterSpacing: "-0.03em",
            }}
          >
            Customer <em style={{ color: BRAND.gold, fontWeight: 400 }}>portal.</em>
          </SerifHeading>
        </div>

        {loading && (
          <div
            className="flex justify-center py-16"
            style={{ color: palette.muted }}
          >
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {!loading && error && (
          <Alert
            className="mb-6"
            style={{
              background: BRAND.terracotta + "15",
              borderColor: BRAND.terracotta + "40",
              color: BRAND.terracotta,
            }}
          >
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && notFound && (
          <EditorialCard variant="ivory" className="p-8 text-center sm:p-12">
            <SerifHeading level={2} className="mb-4" style={{ fontSize: 28, fontWeight: 500 }}>
              No project linked yet.
            </SerifHeading>
            <p
              className="mx-auto mb-8 max-w-md text-[15px]"
              style={{ color: palette.ink2, lineHeight: 1.6 }}
            >
              No Solutions project is linked to this account yet. If you
              recently submitted a request, our team will set up your project
              after your meeting.
            </p>
            <PillButton variant="primary" onClick={() => navigate("/solutions")}>
              About Muslim+ Solutions
              <span aria-hidden>→</span>
            </PillButton>
          </EditorialCard>
        )}

        {!loading && client && (
          <div className="space-y-8">
            {/* Anything the customer must act on, before the rest of the page */}
            {client.paymentActionRequired && (
              <Alert
                style={{
                  background: BRAND.gold + "18",
                  borderColor: BRAND.gold + "55",
                  color: BRAND.goldDeep,
                }}
              >
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>
                  Your bank needs you to confirm the latest payment. Open Manage billing
                  below to complete it, otherwise your service may be interrupted.
                </AlertDescription>
              </Alert>
            )}

            {/* Behind on payment, but the service is still running. Say both
                halves plainly: nothing is switched off yet, and here is the
                date by which it needs fixing. */}
            {client.paymentGraceEndsAt && client.accessEnabled && (
              <Alert
                style={{
                  background: BRAND.gold + "18",
                  borderColor: BRAND.gold + "55",
                  color: BRAND.goldDeep,
                }}
              >
                <ShieldAlert className="h-4 w-4" />
                <AlertDescription>
                  Your last payment did not go through, and we are retrying your card. Your
                  service is still running. Update your payment details under Manage billing
                  by {formatDate(client.paymentGraceEndsAt) || "the end of the grace period"},
                  after which it will be paused.
                </AlertDescription>
              </Alert>
            )}

            {!client.accessEnabled && client.suspendedReason && (
              <Alert
                style={{
                  background: BRAND.terracotta + "15",
                  borderColor: BRAND.terracotta + "40",
                  color: BRAND.terracotta,
                }}
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {client.suspendedReason === "payment_failed" ? (
                    <>
                      Your service is paused because the last payment did not go through.
                      Update your card under Manage billing and access is restored
                      automatically.
                    </>
                  ) : client.suspendedReason === "subscription_canceled" ? (
                    <>Your subscription has ended, so your solution is switched off.</>
                  ) : (
                    <>
                      Your service is currently paused. Contact us at{" "}
                      <a href="mailto:support@muslimplus.org" className="underline">
                        support@muslimplus.org
                      </a>{" "}
                      and we will sort it out.
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {client.cancelAtPeriodEnd && (
              <Alert
                style={{
                  background: BRAND.muted + "15",
                  borderColor: BRAND.muted + "40",
                  color: palette.ink2,
                }}
              >
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex flex-wrap items-center gap-3">
                  <span>
                    Your subscription is set to end
                    {formatDate(client.accessEndsAt)
                      ? ` on ${formatDate(client.accessEndsAt)}`
                      : " at the end of the current period"}
                    . You will not be charged again.
                  </span>
                  <PillButton
                    variant="outline"
                    size="sm"
                    onClick={handleReactivate}
                    disabled={cancelBusy}
                  >
                    {cancelBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <RotateCcw className="h-4 w-4" />
                        Keep my subscription
                      </>
                    )}
                  </PillButton>
                </AlertDescription>
              </Alert>
            )}

            {cancelNotice && (
              <Alert
                style={{
                  background: BRAND.sage + "15",
                  borderColor: BRAND.sage + "40",
                  color: BRAND.sage,
                }}
              >
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{cancelNotice}</AlertDescription>
              </Alert>
            )}

            {/* Project overview */}
            <EditorialCard variant="ivory" className="p-6 sm:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="mb-2" style={labelStyle}>
                    Project
                  </div>
                  <SerifHeading
                    level={2}
                    className="break-words"
                    style={{ fontSize: "clamp(24px, 5vw, 30px)", fontWeight: 500 }}
                  >
                    {client.businessName}
                  </SerifHeading>
                </div>
                <StatusPill status={client.status} styles={CLIENT_STATUS_STYLES} />
              </div>

              <div className="grid gap-6 sm:grid-cols-3">
                <div>
                  <div className="mb-1.5" style={labelStyle}>
                    Offer
                  </div>
                  {downPayment || monthly ? (
                    <p className="text-[15px]" style={{ color: palette.ink, lineHeight: 1.5 }}>
                      {downPayment && (
                        <>
                          <span style={{ fontWeight: 600 }}>{downPayment}</span> one
                          time
                        </>
                      )}
                      {downPayment && monthly && <br />}
                      {monthly && (
                        <>
                          <span style={{ fontWeight: 600 }}>{monthly}</span> per
                          month support{billingOnHold ? ", from launch day" : ""}
                        </>
                      )}
                    </p>
                  ) : (
                    <p className="text-[15px]" style={{ color: palette.muted }}>
                      No offer yet
                    </p>
                  )}
                  {offer?.description && (
                    <p
                      className="mt-2 break-words text-sm"
                      style={{ color: palette.ink2, lineHeight: 1.55 }}
                    >
                      {offer.description}
                    </p>
                  )}
                </div>
                <div>
                  <div className="mb-1.5" style={labelStyle}>
                    Next payment
                  </div>
                  <p className="text-[15px]" style={{ color: palette.ink }}>
                    {billingOnHold
                      ? billingStarts
                        ? `${billingStarts}, when your monthly support starts`
                        : "None yet. Your monthly support starts when your project goes live."
                      : nextPayment || "Not scheduled"}
                  </p>
                </div>
                <div>
                  <div className="mb-1.5" style={labelStyle}>
                    Access
                  </div>
                  <p
                    className="text-[15px]"
                    style={{
                      color: client.accessEnabled ? BRAND.sage : BRAND.terracotta,
                      fontWeight: 600,
                    }}
                  >
                    {client.accessEnabled ? "Enabled" : "Disabled"}
                  </p>
                </div>
              </div>

              <div
                className="mt-7 flex flex-col gap-3 border-t pt-6 sm:flex-row sm:items-center"
                style={{ borderColor: palette.hairline }}
              >
                <PillButton
                  variant="primary"
                  onClick={handleManageBilling}
                  disabled={billingLoading}
                  className="w-full justify-center sm:w-auto"
                >
                  {billingLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Manage billing
                    </>
                  )}
                </PillButton>
                {billingError && (
                  <span
                    className="break-words text-sm"
                    style={{ color: BRAND.terracotta }}
                  >
                    {billingError}
                  </span>
                )}
              </div>
            </EditorialCard>

            {/* Billing and renewal */}
            <EditorialCard variant="ivory" className="p-6 sm:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <SerifHeading level={2} style={{ fontSize: 26, fontWeight: 500 }}>
                  Billing
                </SerifHeading>
                {paymentMethod && (
                  <span
                    className="inline-flex items-center gap-2 text-sm"
                    style={{ color: palette.ink2 }}
                  >
                    <CreditCard className="h-4 w-4" />
                    {paymentMethod.brand?.toUpperCase()} ending {paymentMethod.last4}
                    <span style={{ color: palette.muted }}>
                      (expires {String(paymentMethod.expMonth).padStart(2, "0")}/
                      {String(paymentMethod.expYear).slice(-2)})
                    </span>
                  </span>
                )}
              </div>

              {/* Automatic renewal disclosure. Required by US state auto renewal
                  laws and the FTC negative option rule: amount, frequency, that
                  it renews until cancelled, and how to cancel. */}
              {monthly && client.hasSubscription && (
                <div
                  className="mb-6 rounded-2xl p-5"
                  style={{
                    background: palette.paper,
                    border: `1px solid ${palette.hairline}`,
                  }}
                >
                  <div className="mb-2" style={labelStyle}>
                    Automatic renewal terms
                  </div>
                  <p className="text-[15px]" style={{ color: palette.ink, lineHeight: 1.6 }}>
                    You are on a recurring charge of{" "}
                    <span style={{ fontWeight: 600 }}>{monthly} per month</span>. It renews
                    automatically until you cancel
                    {billingOnHold ? (
                      <>
                        , and it has not started yet. Your first monthly charge is taken{" "}
                        <span style={{ fontWeight: 600 }}>
                          {billingStarts || "on the day your project goes live"}
                        </span>
                        , and we email you when it does
                      </>
                    ) : nextPayment ? (
                      <>
                        , and the next charge is on{" "}
                        <span style={{ fontWeight: 600 }}>{nextPayment}</span>
                      </>
                    ) : null}
                    .{" "}
                    {downPayment ? (
                      <>
                        The {downPayment} down payment was a one time charge and does not
                        repeat.{" "}
                      </>
                    ) : null}
                    You can cancel at any time, with no cancellation fee, using the button
                    below. Cancelling stops all future charges and you keep access until the
                    end of the period you have already paid for. We will always tell you in
                    advance before the monthly amount changes.
                  </p>

                  {/* A change that has been noticed but has not started yet. The
                      amount above is what is still being charged until this date,
                      so both figures have to be on screen together. */}
                  {client.pendingPriceChange && (
                    <p
                      className="mt-4 border-t pt-4 text-[15px]"
                      style={{ color: palette.ink, borderColor: palette.hairline, lineHeight: 1.6 }}
                    >
                      <span style={{ fontWeight: 600 }}>Your monthly amount is changing.</span>{" "}
                      You are still being charged {monthly} per month. From{" "}
                      <span style={{ fontWeight: 600 }}>
                        {billingOnHold
                          ? "the day your monthly support starts"
                          : formatDate(client.pendingPriceChange.effectiveAt)}
                      </span>{" "}
                      it becomes{" "}
                      <span style={{ fontWeight: 600 }}>
                        {formatMoney(client.pendingPriceChange.monthly)} per month
                      </span>
                      . Nothing changes before then. If you would rather not continue at the
                      new amount, cancel before that date and you will not be charged it.
                    </p>
                  )}
                </div>
              )}

              {/* Billing history */}
              <div className="mb-2" style={labelStyle}>
                Billing history
              </div>
              {invoicesError && (
                <Alert
                  className="mt-3"
                  style={{
                    background: BRAND.terracotta + "15",
                    borderColor: BRAND.terracotta + "40",
                    color: BRAND.terracotta,
                  }}
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{invoicesError}</AlertDescription>
                </Alert>
              )}
              {invoicesLoading ? (
                <div className="flex justify-center py-8" style={{ color: palette.muted }}>
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : invoices.length === 0 && !invoicesError ? (
                <p className="py-4 text-sm" style={{ color: palette.muted }}>
                  No payments yet. Your invoices and receipts will appear here.
                </p>
              ) : (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-left text-sm" style={{ minWidth: 520 }}>
                    <thead>
                      <tr style={{ color: palette.muted }}>
                        <th className="pb-2 pr-4 font-medium">Date</th>
                        <th className="pb-2 pr-4 font-medium">Description</th>
                        <th className="pb-2 pr-4 font-medium">Amount</th>
                        <th className="pb-2 pr-4 font-medium">Status</th>
                        <th className="pb-2 font-medium">Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr
                          key={inv.id}
                          style={{ borderTop: `1px solid ${palette.hairline}` }}
                        >
                          <td className="py-3 pr-4" style={{ color: palette.ink }}>
                            {formatDate(inv.created) || "-"}
                          </td>
                          <td className="py-3 pr-4" style={{ color: palette.ink2 }}>
                            {inv.lines.length
                              ? inv.lines.map((l, i) => (
                                  <div key={i}>
                                    {l.description || (l.recurring ? "Monthly support" : "One time charge")}
                                  </div>
                                ))
                              : "Subscription"}
                          </td>
                          <td
                            className="py-3 pr-4 whitespace-nowrap"
                            style={{ color: palette.ink, fontWeight: 600 }}
                          >
                            ${inv.total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 pr-4">
                            <StatusPill status={inv.status} styles={INVOICE_STATUS_STYLES} />
                          </td>
                          <td className="py-3">
                            {invoiceLink(inv) ? (
                              <a
                                href={invoiceLink(inv)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 underline transition-colors hover:text-[#bf9a2f]"
                                style={{ color: palette.ink2 }}
                              >
                                <Download className="h-3.5 w-3.5" />
                                PDF
                              </a>
                            ) : (
                              <span style={{ color: palette.muted }}>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Cancellation. Kept as a plain visible control on the same page,
                  no retention funnel, so cancelling is as easy as signing up. */}
              {client.hasSubscription && !client.cancelAtPeriodEnd && (
                <div
                  className="mt-7 border-t pt-6"
                  style={{ borderColor: palette.hairline }}
                >
                  {cancelError && (
                    <Alert
                      className="mb-4"
                      style={{
                        background: BRAND.terracotta + "15",
                        borderColor: BRAND.terracotta + "40",
                        color: BRAND.terracotta,
                      }}
                    >
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{cancelError}</AlertDescription>
                    </Alert>
                  )}
                  {!showCancel ? (
                    <button
                      type="button"
                      onClick={() => setShowCancel(true)}
                      className="text-sm underline transition-colors hover:text-[#bf9a2f]"
                      style={{ color: palette.muted }}
                    >
                      Cancel my subscription
                    </button>
                  ) : (
                    <form onSubmit={handleCancel} className="space-y-4">
                      <p className="text-[15px]" style={{ color: palette.ink, lineHeight: 1.6 }}>
                        Cancelling stops all future charges. You keep access until{" "}
                        {nextPayment || "the end of the period you have paid for"}, then your
                        solution is switched off. There is no cancellation fee. The one time
                        down payment is not refunded, as it covers work already delivered.
                      </p>
                      <div className="space-y-2">
                        <label htmlFor="cancel-reason" style={labelStyle}>
                          Anything we could have done better? (optional)
                        </label>
                        <Input
                          id="cancel-reason"
                          value={cancelReason}
                          onChange={(e) => setCancelReason(e.target.value)}
                          placeholder="Optional feedback"
                          className={inputClass}
                          style={{ ...inputStyle, background: palette.ivory }}
                        />
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <PillButton type="submit" variant="primary" size="sm" disabled={cancelBusy}>
                          {cancelBusy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Confirm cancellation"
                          )}
                        </PillButton>
                        <PillButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowCancel(false)}
                        >
                          Never mind
                        </PillButton>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </EditorialCard>

            {/* Change requests */}
            <EditorialCard variant="ivory" className="p-6 sm:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <SerifHeading level={2} style={{ fontSize: 26, fontWeight: 500 }}>
                  Change requests
                </SerifHeading>
                <PillButton
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowNewRequest((v) => !v);
                    setCreateError("");
                    setCreateSuccess("");
                  }}
                >
                  <Plus className="h-4 w-4" />
                  New request
                </PillButton>
              </div>

              {createSuccess && (
                <Alert
                  className="mb-5"
                  style={{
                    background: BRAND.sage + "15",
                    borderColor: BRAND.sage + "40",
                    color: BRAND.sage,
                  }}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>{createSuccess}</AlertDescription>
                </Alert>
              )}

              {showNewRequest && (
                <form
                  onSubmit={handleCreateRequest}
                  className="mb-7 space-y-5 rounded-2xl p-5"
                  style={{
                    background: palette.paper,
                    border: `1px solid ${palette.hairline}`,
                  }}
                >
                  {createError && (
                    <Alert
                      style={{
                        background: BRAND.terracotta + "15",
                        borderColor: BRAND.terracotta + "40",
                        color: BRAND.terracotta,
                      }}
                    >
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{createError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="grid gap-5 sm:grid-cols-[1fr_180px]">
                    <div className="space-y-2">
                      <label htmlFor="cr-title" style={labelStyle}>
                        Title *
                      </label>
                      <Input
                        id="cr-title"
                        value={newRequest.title}
                        onChange={(e) =>
                          setNewRequest((p) => ({ ...p, title: e.target.value }))
                        }
                        placeholder="Update the homepage banner"
                        className={inputClass}
                        style={{ ...inputStyle, background: palette.ivory }}
                      />
                    </div>
                    <div className="space-y-2">
                      <label style={labelStyle}>Priority</label>
                      <Select
                        value={newRequest.priority}
                        onValueChange={(v) =>
                          setNewRequest((p) => ({ ...p, priority: v }))
                        }
                      >
                        <SelectTrigger
                          className={inputClass}
                          style={{ ...inputStyle, background: palette.ivory }}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map((p) => (
                            <SelectItem key={p} value={p}>
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="cr-description" style={labelStyle}>
                      Description *
                    </label>
                    <Textarea
                      id="cr-description"
                      value={newRequest.description}
                      onChange={(e) =>
                        setNewRequest((p) => ({ ...p, description: e.target.value }))
                      }
                      placeholder="Describe the change you need."
                      rows={4}
                      className="rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]"
                      style={{ ...inputStyle, background: palette.ivory }}
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <PillButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewRequest(false)}
                    >
                      Cancel
                    </PillButton>
                    <PillButton type="submit" variant="primary" size="sm" disabled={creating}>
                      {creating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Send request"
                      )}
                    </PillButton>
                  </div>
                </form>
              )}

              {requestsError && (
                <Alert
                  className="mb-5"
                  style={{
                    background: BRAND.terracotta + "15",
                    borderColor: BRAND.terracotta + "40",
                    color: BRAND.terracotta,
                  }}
                >
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{requestsError}</AlertDescription>
                </Alert>
              )}

              {requestsLoading ? (
                <div className="flex justify-center py-8" style={{ color: palette.muted }}>
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : changeRequests.length === 0 ? (
                <p className="py-4 text-center text-sm" style={{ color: palette.muted }}>
                  No change requests yet. Use the New request button to send one
                  to the team.
                </p>
              ) : (
                <div className="space-y-4">
                  {changeRequests.map((cr) => (
                    <div
                      key={cr.id}
                      className="rounded-2xl p-5"
                      style={{
                        background: palette.paper,
                        border: `1px solid ${palette.hairline}`,
                      }}
                    >
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <p
                          className="min-w-0 break-words"
                          style={{
                            fontFamily: EDITORIAL_FONT.serif,
                            fontSize: 19,
                            fontWeight: 500,
                            color: palette.ink,
                          }}
                        >
                          {cr.title}
                        </p>
                        <StatusPill status={cr.status} styles={REQUEST_STATUS_STYLES} />
                      </div>
                      <p
                        className="break-words text-sm"
                        style={{ color: palette.ink2, lineHeight: 1.55 }}
                      >
                        {cr.description}
                      </p>
                      <div
                        className="mt-3 flex flex-wrap items-center gap-3 text-xs"
                        style={{ color: palette.muted }}
                      >
                        {cr.priority && (
                          <span className="uppercase" style={{ letterSpacing: "0.12em" }}>
                            {cr.priority} priority
                          </span>
                        )}
                        {formatDate(cr.createdAt) && (
                          <span>Sent {formatDate(cr.createdAt)}</span>
                        )}
                      </div>
                      {cr.response && (
                        <div
                          className="mt-4 break-words rounded-xl p-4 text-sm"
                          style={{
                            background: palette.ivory,
                            border: `1px solid ${palette.hairline}`,
                            color: palette.ink2,
                            lineHeight: 1.55,
                          }}
                        >
                          <div className="mb-1.5" style={labelStyle}>
                            Team response
                          </div>
                          {cr.response}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </EditorialCard>
          </div>
        )}

        <p className="mt-10 text-center text-xs" style={{ color: palette.muted }}>
          Your project is governed by the Muslim+ Solutions{" "}
          <button
            type="button"
            onClick={() => navigate("/solutions/terms")}
            className="underline transition-colors hover:text-[#bf9a2f]"
          >
            Service Terms
          </button>
          .
          {client?.acceptedTerms?.version && (
            <>
              {" "}
              You accepted version {client.acceptedTerms.version}
              {formatDate(client.acceptedTerms.acceptedAt)
                ? ` on ${formatDate(client.acceptedTerms.acceptedAt)}`
                : ""}
              , and that version continues to govern your project.
            </>
          )}
        </p>

        {/* The terms promise a copy of their data on request. This is that
            copy, available without asking anyone. */}
        <p className="mt-3 text-center text-xs" style={{ color: palette.muted }}>
          <button
            type="button"
            onClick={handleExport}
            disabled={exportBusy}
            className="underline transition-colors hover:text-[#bf9a2f] disabled:opacity-60"
          >
            {exportBusy ? "Preparing your file..." : "Download a copy of my data"}
          </button>
          {exportError && (
            <span className="ml-2" style={{ color: BRAND.terracotta }}>
              {exportError}
            </span>
          )}
        </p>
      </main>
    </EditorialShell>
  );
}
